import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Typography,
  LinearProgress,
  Alert,
  Toolbar,
  Tooltip
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FitScreen as FitScreenIcon,
  Error as ErrorIcon,
  NavigateNext as NextIcon,
  NavigateBefore as PrevIcon,
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon
} from '@mui/icons-material';
import ToolbarButton, { ToolbarGroup, ToolbarDivider } from './ToolbarButton';
import { splitPDFContent } from '../../utils/pdfPageSplitter';

const PDFPreview = ({ 
  content, 
  customCSS, 
  customJS, 
  config = {}, 
  title = 'Untitled',
  autoRefresh = true,
  currentEditorPage = 1,
  cursorPosition = null 
}) => {
  const iframeRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [zoom, setZoom] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Format variables in text
  const formatVariables = (text) => {
    if (!text) return '';
    const now = new Date();
    return text
      .replace(/{date}/g, now.toLocaleDateString())
      .replace(/{time}/g, now.toLocaleTimeString())
      .replace(/{page}/g, currentPage.toString())
      .replace(/{total}/g, totalPages.toString())
      .replace(/{title}/g, title || 'Untitled');
  };
  
  // REMOVED - Using splitPDFContent from utils instead
  // Split content into pages
  const splitContentIntoPages_OLD = (htmlContent) => {
    if (!htmlContent) return ['<p>Start typing to see preview...</p>'];

    console.log('Original content:', htmlContent);
    
    // Check if content contains page breaks using string search
    const hasPageBreak = htmlContent.includes('class="page-break"') || 
                        htmlContent.includes('pagebreak') ||
                        htmlContent.includes('page-break');
    
    console.log('Has page break:', hasPageBreak);
    
    if (hasPageBreak) {
      // Split content by page break elements using regex
      // Match various forms of page break elements
      const pageBreakPattern = /<div[^>]*class="[^"]*page-break[^"]*"[^>]*>.*?<\/div>|<div[^>]*data-blot="pagebreak"[^>]*>.*?<\/div>/gi;
      
      // Split the content
      const parts = htmlContent.split(pageBreakPattern);
      
      // Filter and clean parts
      const pages = parts
        .map(part => part.trim())
        .filter(part => part && part.length > 0);
      
      console.log('Split into pages:', pages.length);
      
      if (pages.length > 1) {
        return pages;
      }
      
      // Fallback: Try a different approach
      // Create a temporary div to process the content
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      
      // Find all page break elements
      const allElements = Array.from(tempDiv.children);
      const pagesList = [];
      let currentPage = [];
      
      for (const element of allElements) {
        // Check if it's a page break
        const isPageBreak = element.className && element.className.includes('page-break');
        
        if (isPageBreak) {
          // Save current page if it has content
          if (currentPage.length > 0) {
            pagesList.push(currentPage.map(el => el.outerHTML).join(''));
            currentPage = [];
          }
        } else {
          // Add to current page
          currentPage.push(element);
        }
      }
      
      // Add the last page
      if (currentPage.length > 0) {
        pagesList.push(currentPage.map(el => el.outerHTML).join(''));
      }
      
      console.log('Fallback split into pages:', pagesList.length);
      
      if (pagesList.length > 0) {
        return pagesList;
      }
    }
    
    // For automatic page breaking, attempt to intelligently split content
    return attemptAutomaticPageSplitting(htmlContent);
  };

  // Attempt automatic page splitting for better content flow
  const attemptAutomaticPageSplitting = (htmlContent) => {
    // Calculate approximate content height per page
    const { pageSize = 'A4', orientation = 'portrait', margins = {} } = config;
    const pageSizes = {
      A4: { width: 210, height: 297 },
      Letter: { width: 216, height: 279 },
      Legal: { width: 216, height: 356 }
    };
    
    const dimensions = pageSizes[pageSize] || pageSizes.A4;
    const pageHeight = orientation === 'portrait' ? dimensions.height : dimensions.width;
    const marginTop = margins.top || 20;
    const marginBottom = margins.bottom || 20;
    const contentHeightMM = pageHeight - marginTop - marginBottom;
    
    // Estimate content that would fit on one page
    // This is a rough approximation - actual splitting happens in the browser
    const estimatedWordsPerPage = Math.floor(contentHeightMM / 5); // Roughly 5mm per line
    const estimatedCharsPerPage = estimatedWordsPerPage * 6; // Roughly 6 chars per word
    
    // If content is short enough, return as single page
    if (htmlContent.length <= estimatedCharsPerPage) {
      return [htmlContent];
    }
    
    // Try to split content intelligently by paragraphs
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    const paragraphs = tempDiv.querySelectorAll('p, h1, h2, h3, h4, h5, h6, div, li');
    
    if (paragraphs.length === 0) {
      return [htmlContent]; // Return as single page if no paragraphs found
    }
    
    const pages = [];
    let currentPage = '';
    let currentPageLength = 0;
    
    paragraphs.forEach((para) => {
      const paraHTML = para.outerHTML;
      const paraLength = paraHTML.length;
      
      // If adding this paragraph would exceed the page limit, start a new page
      if (currentPageLength + paraLength > estimatedCharsPerPage && currentPage.trim()) {
        pages.push(currentPage.trim());
        currentPage = paraHTML;
        currentPageLength = paraLength;
      } else {
        currentPage += paraHTML;
        currentPageLength += paraLength;
      }
    });
    
    // Add the last page
    if (currentPage.trim()) {
      pages.push(currentPage.trim());
    }
    
    return pages.length > 0 ? pages : [htmlContent];
  };

  // Generate preview HTML
  const generatePreviewHTML = () => {
    const { pageSize = 'A4', orientation = 'portrait', margins = {} } = config;
    const { watermark = {}, headerFooter = {} } = config;
    
    // Debug watermark settings
    console.log('Watermark config:', watermark);
    console.log('Pattern enabled:', watermark.pattern?.enabled);
    console.log('Pattern spacing:', watermark.pattern?.spacing);
    console.log('Blend mode:', watermark.blendMode);
    console.log('Rotation:', watermark.rotation);
    
    // Page dimensions based on size and orientation
    const pageSizes = {
      A4: { width: 210, height: 297 },
      Letter: { width: 216, height: 279 },
      Legal: { width: 216, height: 356 }
    };
    
    const dimensions = pageSizes[pageSize] || pageSizes.A4;
    const pageWidth = orientation === 'portrait' ? dimensions.width : dimensions.height;
    const pageHeight = orientation === 'portrait' ? dimensions.height : dimensions.width;
    
    // Extract inline styles from content if present
    let extractedStyles = '';
    let cleanedContent = content;
    
    // Check if content contains style tags
    if (content && content.includes('<style')) {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content;
      
      // Extract all style tags
      const styleTags = tempDiv.querySelectorAll('style');
      styleTags.forEach(styleTag => {
        extractedStyles += styleTag.textContent + '\n';
        // Remove the style tag from content
        styleTag.remove();
      });
      
      // Get the cleaned content without style tags
      cleanedContent = tempDiv.innerHTML;
    }
    
    // Split content into pages using the utility (this will now work with automatic page breaks from editor)
    const contentPages = splitPDFContent(cleanedContent);
    
    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${title}</title>
          <link href="https://fonts.googleapis.com/css2?family=Cairo:wght@200;300;400;500;600;700;800;900&family=Roboto:wght@100;300;400;500;700;900&family=Open+Sans:wght@300;400;500;600;700;800&family=Lato:wght@100;300;400;700;900&family=Montserrat:wght@100;200;300;400;500;600;700;800;900&family=Poppins:wght@100;200;300;400;500;600;700;800;900&family=Tajawal:wght@200;300;400;500;700;800;900&display=swap" rel="stylesheet">
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            /* Page break styles for manual breaks - hidden in preview */
            .page-break {
              display: none !important;
            }
            
            /* Content page-break should not show in preview */
            .page-content .page-break {
              display: none !important;
            }
            
            /* For print and PDF generation */
            @media print {
              .page-break {
                visibility: hidden;
                page-break-after: always;
                break-after: page;
              }
              
              .page {
                margin-bottom: 0 !important;
              }
            }
            
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
              line-height: 1.6;
              color: #333;
              background: #f5f5f5;
              padding: 0;
              margin: 0;
              width: 100%;
              overflow-x: hidden;
              display: flex;
              flex-direction: column;
              align-items: center;
            }
            
            /* Container for pages with proper spacing */
            body > .page:first-child {
              margin-top: 20px;
            }
            
            .page {
              width: ${pageWidth}mm;
              height: ${pageHeight}mm;
              min-height: ${pageHeight}mm;
              max-height: ${pageHeight}mm;
              background: white;
              margin: 0 auto 40px;
              padding: ${margins.top || 20}mm ${margins.right || 20}mm ${margins.bottom || 20}mm ${margins.left || 20}mm;
              box-shadow: 0 4px 20px rgba(0,0,0,0.12);
              position: relative;
              overflow: hidden;
              page-break-after: always;
              break-after: page;
              display: flex;
              flex-direction: column;
              border: 1px solid #ddd;
              box-sizing: border-box;
            }
            
            /* Visual page separation - clearer distinction */
            .page:not(:last-child)::after {
              content: '';
              position: absolute;
              bottom: -20px;
              left: 50%;
              transform: translateX(-50%);
              width: 80%;
              height: 1px;
              background: linear-gradient(90deg, transparent, #ccc 20%, #ccc 80%, transparent);
            }
            
            .page-container {
              background: #f0f0f0;
              padding: 30px 20px;
              min-height: 100vh;
              width: 100% !important;
              overflow-x: auto;
              overflow-y: visible;
            }
            
            /* Overflow page styling - DISABLED since we have automatic pagination */
            /*
            .overflow-page {
              border-left: 4px solid #ff9800 !important;
              position: relative !important;
            }
            
            .overflow-page::after {
              content: "⚠️ Content overflow detected";
              position: absolute;
              top: 10px;
              left: 10px;
              background: rgba(255, 152, 0, 0.9);
              color: white;
              padding: 4px 8px;
              font-size: 11px;
              border-radius: 3px;
              z-index: 1000;
              pointer-events: none;
            }
            */
            
            /* Ensure proper page height and no overflow */
            @media screen {
              .page {
                height: ${pageHeight}mm !important;
                overflow: hidden !important;
              }
              
              .page-content {
                flex: 1;
                overflow: hidden !important;
                max-height: calc(${pageHeight}mm - ${(margins.top || 20) + (margins.bottom || 20)}mm);
                position: relative;
              }
            }
            
            /* Page number indicator */
            .page-number {
              position: absolute;
              bottom: 10mm;
              left: 50%;
              transform: translateX(-50%);
              font-size: 10pt;
              color: #666;
              z-index: 100;
            }
            
            /* Hide page breaks in preview but keep structure */
            .page-content {
              position: relative;
            }
            
            ${watermark.enabled ? `
            .watermark-container {
              position: absolute;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              overflow: hidden;
              pointer-events: none;
              z-index: 10;
              /* Ensure blend mode works properly */
              isolation: isolate;
            }
            /* Base watermark styles (used in pattern mode - no positioning) */
            .watermark {
              font-size: ${watermark.textSize || 60}px;
              font-weight: ${watermark.fontStyle?.bold ? 'bold' : 'normal'};
              font-style: ${watermark.fontStyle?.italic ? 'italic' : 'normal'};
              font-family: ${watermark.fontFamily || 'Arial'}, sans-serif;
              color: ${watermark.color || 'rgba(0, 0, 0, 0.3)'};
              opacity: ${watermark.opacity || 0.3};
              pointer-events: none;
              user-select: none;
              text-transform: ${watermark.textTransform || 'none'};
              mix-blend-mode: ${watermark.blendMode || 'normal'};
              ${watermark.border?.enabled ? `
                text-shadow: 
                  -${watermark.border.width}px -${watermark.border.width}px 0 ${watermark.border.color},  
                   ${watermark.border.width}px -${watermark.border.width}px 0 ${watermark.border.color},
                  -${watermark.border.width}px  ${watermark.border.width}px 0 ${watermark.border.color},
                   ${watermark.border.width}px  ${watermark.border.width}px 0 ${watermark.border.color};
              ` : ''}
            }
            .watermark-image {
              opacity: ${watermark.opacity || 0.3};
              pointer-events: none;
              user-select: none;
              width: ${(watermark.imageScale || 0.5) * 200}px;
              max-width: ${(watermark.imageScale || 0.5) * 200}px;
              height: auto;
              mix-blend-mode: ${watermark.blendMode || 'normal'};
            }
            .watermark-pattern {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              display: grid;
              grid-template-columns: repeat(auto-fill, minmax(${Math.max(watermark.image ? (watermark.imageScale || 0.5) * 200 : watermark.textSize || 60, 50)}px, max-content));
              gap: ${watermark.pattern?.spacing?.vertical || 150}px ${watermark.pattern?.spacing?.horizontal || 150}px;
              justify-content: start;
              align-content: start;
              overflow: hidden;
              z-index: 10;
              pointer-events: none;
              padding: 20px;
            }
            /* Pattern mode styles - clean grid layout */
            .watermark-pattern .watermark {
              transform: rotate(${watermark.rotation !== undefined ? watermark.rotation : 0}deg);
            }
            .watermark-pattern .watermark-image {
              transform: rotate(${watermark.rotation !== undefined ? watermark.rotation : 0}deg);
            }
            ` : ''}
            
            /* Single watermark styles (when pattern is disabled) */
            .watermark-single {
              position: absolute;
              font-size: ${watermark.textSize || 60}px;
              font-weight: ${watermark.fontStyle?.bold ? 'bold' : 'normal'};
              font-style: ${watermark.fontStyle?.italic ? 'italic' : 'normal'};
              font-family: ${watermark.fontFamily || 'Arial'}, sans-serif;
              color: ${watermark.color || 'rgba(0, 0, 0, 0.3)'};
              opacity: ${watermark.opacity || 0.3};
              z-index: 10;
              pointer-events: none;
              user-select: none;
              text-transform: ${watermark.textTransform || 'none'};
              mix-blend-mode: ${watermark.blendMode || 'normal'};
              ${watermark.border?.enabled ? `
                text-shadow: 
                  -${watermark.border.width}px -${watermark.border.width}px 0 ${watermark.border.color},  
                   ${watermark.border.width}px -${watermark.border.width}px 0 ${watermark.border.color},
                  -${watermark.border.width}px  ${watermark.border.width}px 0 ${watermark.border.color},
                   ${watermark.border.width}px  ${watermark.border.width}px 0 ${watermark.border.color};
              ` : ''}
              ${watermark.position === 'center' ? `
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%) rotate(${watermark.rotation !== undefined ? watermark.rotation : 0}deg);
              ` : ''}
              ${watermark.position === 'diagonal' ? `
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%) rotate(${watermark.rotation !== undefined ? watermark.rotation : 0}deg);
              ` : ''}
              ${watermark.position === 'top-left' ? `
                top: 20px;
                left: 20px;
                transform: rotate(${watermark.rotation || 0}deg);
              ` : ''}
              ${watermark.position === 'top-right' ? `
                top: 20px;
                right: 20px;
                transform: rotate(${watermark.rotation || 0}deg);
              ` : ''}
              ${watermark.position === 'bottom-left' ? `
                bottom: 20px;
                left: 20px;
                transform: rotate(${watermark.rotation || 0}deg);
              ` : ''}
              ${watermark.position === 'bottom-right' ? `
                bottom: 20px;
                right: 20px;
                transform: rotate(${watermark.rotation || 0}deg);
              ` : ''}
              ${watermark.position === 'custom' ? `
                top: ${watermark.customPosition?.y || 50}%;
                left: ${watermark.customPosition?.x || 50}%;
                transform: translate(-50%, -50%) rotate(${watermark.rotation || 0}deg);
              ` : ''}
            }
            .watermark-image-single {
              position: absolute;
              opacity: ${watermark.opacity || 0.3};
              z-index: 10;
              pointer-events: none;
              user-select: none;
              width: ${(watermark.imageScale || 0.5) * 200}px;
              max-width: ${(watermark.imageScale || 0.5) * 200}px;
              height: auto;
              mix-blend-mode: ${watermark.blendMode || 'normal'};
              ${watermark.position === 'center' ? `
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%) rotate(${watermark.rotation !== undefined ? watermark.rotation : 0}deg);
              ` : ''}
              ${watermark.position === 'diagonal' ? `
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%) rotate(${watermark.rotation !== undefined ? watermark.rotation : 0}deg);
              ` : ''}
              ${watermark.position === 'top-left' ? `
                top: 20px;
                left: 20px;
                transform: rotate(${watermark.rotation || 0}deg);
              ` : ''}
              ${watermark.position === 'top-right' ? `
                top: 20px;
                right: 20px;
                transform: rotate(${watermark.rotation || 0}deg);
              ` : ''}
              ${watermark.position === 'bottom-left' ? `
                bottom: 20px;
                left: 20px;
                transform: rotate(${watermark.rotation || 0}deg);
              ` : ''}
              ${watermark.position === 'bottom-right' ? `
                bottom: 20px;
                right: 20px;
                transform: rotate(${watermark.rotation || 0}deg);
              ` : ''}
              ${watermark.position === 'custom' ? `
                top: ${watermark.customPosition?.y || 50}%;
                left: ${watermark.customPosition?.x || 50}%;
                transform: translate(-50%, -50%) rotate(${watermark.rotation || 0}deg);
              ` : ''}
            }
            
            .content {
              position: relative;
              z-index: 2;
              /* Ensure content width matches editor */
              width: 100%;
              /* Clear floats properly */
              overflow: auto;
            }
            
            /* Ensure paragraphs clear floats properly */
            .content p {
              margin: 0 0 1em 0;
            }
            
            /* Clear fix for floated elements */
            .content::after {
              content: "";
              display: table;
              clear: both;
            }
            
            /* Default styles for common elements */
            h1 {
              font-size: 2em;
              margin: 0.67em 0;
              font-weight: 600;
            }
            
            h2 {
              font-size: 1.5em;
              margin: 0.75em 0;
              font-weight: 600;
            }
            
            h3 {
              font-size: 1.17em;
              margin: 0.83em 0;
              font-weight: 600;
            }
            
            h4 {
              font-size: 1em;
              margin: 1.12em 0;
              font-weight: 600;
            }
            
            h5 {
              font-size: 0.83em;
              margin: 1.5em 0;
              font-weight: 600;
            }
            
            h6 {
              font-size: 0.75em;
              margin: 1.67em 0;
              font-weight: 600;
            }
            
            p {
              margin-bottom: 1em;
              line-height: 1.8;
            }
            
            /* Font families */
            .ql-font-serif {
              font-family: Georgia, Times, 'Times New Roman', serif;
            }
            
            .ql-font-monospace {
              font-family: Monaco, 'Courier New', monospace;
            }
            
            .ql-font-cairo {
              font-family: 'Cairo', sans-serif;
            }
            
            .ql-font-roboto {
              font-family: 'Roboto', sans-serif;
            }
            
            .ql-font-opensans {
              font-family: 'Open Sans', sans-serif;
            }
            
            .ql-font-lato {
              font-family: 'Lato', sans-serif;
            }
            
            .ql-font-montserrat {
              font-family: 'Montserrat', sans-serif;
            }
            
            .ql-font-poppins {
              font-family: 'Poppins', sans-serif;
            }
            
            .ql-font-tajawal {
              font-family: 'Tajawal', sans-serif;
            }
            
            /* Font sizes */
            [style*="font-size: 10px"] {
              font-size: 10px !important;
            }
            
            [style*="font-size: 12px"] {
              font-size: 12px !important;
            }
            
            [style*="font-size: 14px"] {
              font-size: 14px !important;
            }
            
            [style*="font-size: 16px"] {
              font-size: 16px !important;
            }
            
            [style*="font-size: 18px"] {
              font-size: 18px !important;
            }
            
            [style*="font-size: 20px"] {
              font-size: 20px !important;
            }
            
            [style*="font-size: 24px"] {
              font-size: 24px !important;
            }
            
            [style*="font-size: 28px"] {
              font-size: 28px !important;
            }
            
            [style*="font-size: 32px"] {
              font-size: 32px !important;
            }
            
            [style*="font-size: 36px"] {
              font-size: 36px !important;
            }
            
            [style*="font-size: 48px"] {
              font-size: 48px !important;
            }
            
            [style*="font-size: 64px"] {
              font-size: 64px !important;
            }
            
            /* Font weights - CRITICAL for matching editor */
            [style*="font-weight: 100"] {
              font-weight: 100 !important;
            }
            
            [style*="font-weight: 200"] {
              font-weight: 200 !important;
            }
            
            [style*="font-weight: 300"] {
              font-weight: 300 !important;
            }
            
            [style*="font-weight: 400"] {
              font-weight: 400 !important;
            }
            
            [style*="font-weight: 500"] {
              font-weight: 500 !important;
            }
            
            [style*="font-weight: 600"] {
              font-weight: 600 !important;
            }
            
            [style*="font-weight: 700"], strong, b {
              font-weight: 700 !important;
            }
            
            [style*="font-weight: 800"] {
              font-weight: 800 !important;
            }
            
            [style*="font-weight: 900"] {
              font-weight: 900 !important;
            }
            
            /* Text formatting styles */
            em, i {
              font-style: italic !important;
            }
            
            u {
              text-decoration: underline !important;
            }
            
            s, strike {
              text-decoration: line-through !important;
            }
            
            /* Subscript and Superscript */
            sub {
              vertical-align: sub;
              font-size: smaller;
            }
            
            sup {
              vertical-align: super;
              font-size: smaller;
            }
            
            /* Code blocks and inline code */
            code {
              background-color: #f4f4f4;
              padding: 2px 4px;
              border-radius: 3px;
              font-family: 'Courier New', monospace;
              font-size: 0.9em;
            }
            
            pre {
              background-color: #f4f4f4;
              padding: 10px;
              border-radius: 5px;
              overflow-x: auto;
              font-family: 'Courier New', monospace;
            }
            
            pre code {
              background-color: transparent;
              padding: 0;
            }
            
            /* Links */
            a {
              color: #0066cc;
              text-decoration: underline;
            }
            
            a:hover {
              color: #0052a3;
            }
            
            /* Color and background color handling */
            /* These will be applied inline by Quill, but we ensure they work */
            [style*="color:"] {
              color: inherit;
            }
            
            [style*="background-color:"] {
              background-color: inherit;
            }
            
            /* Interactive elements - buttons and inputs from editor */
            .button-wrapper {
              display: inline-block;
              margin: 5px;
            }
            
            .button-wrapper button {
              padding: 8px 16px;
              border-radius: 4px;
              border: 1px solid #ddd;
              background: linear-gradient(to bottom, #ffffff, #f0f0f0);
              cursor: pointer;
              font-family: inherit;
              font-size: 14px;
              transition: all 0.3s ease;
            }
            
            .button-wrapper button:hover {
              background: linear-gradient(to bottom, #f0f0f0, #e0e0e0);
              box-shadow: 0 2px 4px rgba(0,0,0,0.1);
            }
            
            .button-wrapper button.primary {
              background: linear-gradient(to bottom, #4CAF50, #45a049);
              color: white;
              border-color: #45a049;
            }
            
            .button-wrapper button.secondary {
              background: linear-gradient(to bottom, #008CBA, #007399);
              color: white;
              border-color: #007399;
            }
            
            .button-wrapper button.danger {
              background: linear-gradient(to bottom, #f44336, #da190b);
              color: white;
              border-color: #da190b;
            }
            
            .button-wrapper button.warning {
              background: linear-gradient(to bottom, #ff9800, #e68900);
              color: white;
              border-color: #e68900;
            }
            
            .button-wrapper button.info {
              background: linear-gradient(to bottom, #2196F3, #0b7dda);
              color: white;
              border-color: #0b7dda;
            }
            
            .button-wrapper button.success {
              background: linear-gradient(to bottom, #4CAF50, #45a049);
              color: white;
              border-color: #45a049;
            }
            
            .button-wrapper button.dark {
              background: linear-gradient(to bottom, #555555, #333333);
              color: white;
              border-color: #333333;
            }
            
            .button-wrapper button.light {
              background: linear-gradient(to bottom, #f1f1f1, #ffffff);
              color: #333;
              border-color: #ddd;
            }
            
            .button-wrapper button.outline {
              background: transparent;
              color: #333;
              border: 2px solid currentColor;
            }
            
            .button-wrapper button.rounded {
              border-radius: 50px;
            }
            
            .button-wrapper button.small {
              padding: 4px 8px;
              font-size: 12px;
            }
            
            .button-wrapper button.large {
              padding: 12px 24px;
              font-size: 18px;
            }
            
            /* Input fields from editor */
            .input-wrapper {
              display: inline-block;
              margin: 5px;
              vertical-align: middle;
            }
            
            .input-wrapper input,
            .input-wrapper textarea,
            .input-wrapper select {
              padding: 6px 10px;
              border: 1px solid #ddd;
              border-radius: 4px;
              font-family: inherit;
              font-size: 14px;
              background: white;
              transition: all 0.3s ease;
            }
            
            .input-wrapper input:focus,
            .input-wrapper textarea:focus,
            .input-wrapper select:focus {
              outline: none;
              border-color: #4CAF50;
              box-shadow: 0 0 5px rgba(76, 175, 80, 0.3);
            }
            
            .input-wrapper input[type="checkbox"],
            .input-wrapper input[type="radio"] {
              margin-right: 5px;
              vertical-align: middle;
            }
            
            .input-wrapper label {
              margin-right: 10px;
              font-weight: 500;
            }
            
            .input-wrapper textarea {
              vertical-align: top;
              min-height: 60px;
              resize: vertical;
            }
            
            .input-wrapper select {
              cursor: pointer;
            }
            
            .input-wrapper .required::after {
              content: ' *';
              color: red;
            }
            
            /* Blockquote styling */
            blockquote {
              border-left: 4px solid #ccc;
              margin: 1.5em 10px;
              padding: 0.5em 10px;
              quotes: "\\201C""\\201D""\\2018""\\2019";
              background: #f9f9f9;
              font-style: italic;
              position: relative;
            }
            
            blockquote:before {
              color: #ccc;
              content: open-quote;
              font-size: 4em;
              line-height: 0.1em;
              margin-right: 0.25em;
              vertical-align: -0.4em;
            }
            
            blockquote p {
              display: inline;
            }
            
            /* Alignment Support */
            .ql-align-left {
              text-align: left !important;
            }
            
            .ql-align-center {
              text-align: center !important;
            }
            
            .ql-align-right {
              text-align: right !important;
            }
            
            .ql-align-justify {
              text-align: justify !important;
            }
            
            /* RTL Support */
            .ql-direction-rtl {
              direction: rtl !important;
              text-align: right !important;
            }
            
            /* Indent handling */
            .ql-indent-1 { padding-left: 3em !important; }
            .ql-indent-2 { padding-left: 6em !important; }
            .ql-indent-3 { padding-left: 9em !important; }
            .ql-indent-4 { padding-left: 12em !important; }
            .ql-indent-5 { padding-left: 15em !important; }
            .ql-indent-6 { padding-left: 18em !important; }
            .ql-indent-7 { padding-left: 21em !important; }
            .ql-indent-8 { padding-left: 24em !important; }
            
            /* Lists */
            ol, ul {
              padding-left: 1.5em;
              margin: 1em 0;
            }
            
            li {
              margin: 0.5em 0;
              line-height: 1.8;
            }
            
            table {
              border-collapse: collapse;
              width: 100%;
              margin-bottom: 1em;
            }
            
            table th,
            table td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            
            table th {
              background-color: #f8f9fa;
              font-weight: bold;
            }
            
            img {
              max-width: 100%;
              height: auto;
              display: inline-block;
            }
            
            /* Images with explicit width/height should respect those values */
            img[width] {
              width: attr(width px);
            }
            
            img[height] {
              height: attr(height px);
            }
            
            /* Override max-width for sized images */
            img[width],
            img[style*="width"] {
              max-width: none !important;
            }
            
            /* Ensure image styles are preserved from attributes */
            img {
              /* Default display for proper alignment */
              display: inline-block;
            }
            
            /* Image alignment - only when not floating */
            img[data-align="left"]:not([data-float]) {
              display: block;
              margin-left: 0;
              margin-right: auto;
            }
            
            img[data-align="center"]:not([data-float]) {
              display: block;
              margin-left: auto;
              margin-right: auto;
            }
            
            img[data-align="right"]:not([data-float]) {
              display: block;
              margin-left: auto;
              margin-right: 0;
            }
            
            /* Image float - takes precedence over alignment */
            img[data-float="left"] {
              float: left;
              display: inline;
            }
            
            img[data-float="right"] {
              float: right;
              display: inline;
            }
            
            /* Image margins - applied from data attributes */
            img[data-margin-top] { margin-top: attr(data-margin-top px); }
            img[data-margin-right] { margin-right: attr(data-margin-right px); }
            img[data-margin-bottom] { margin-bottom: attr(data-margin-bottom px); }
            img[data-margin-left] { margin-left: attr(data-margin-left px); }
            
            /* Image border */
            img[data-border-width] {
              border-style: solid;
              border-width: attr(data-border-width px);
              border-color: attr(data-border-color);
              border-radius: attr(data-border-radius px);
            }
            
            /* Image shadow */
            img[data-shadow="small"] { box-shadow: 0 1px 3px rgba(0,0,0,0.12); }
            img[data-shadow="medium"] { box-shadow: 0 4px 6px rgba(0,0,0,0.15); }
            img[data-shadow="large"] { box-shadow: 0 10px 20px rgba(0,0,0,0.19); }
            
            ${headerFooter?.enabled ? `
            /* Header/Footer Styles */
            .page-header {
              position: fixed;
              top: 0;
              left: ${margins.left || 20}mm;
              right: ${margins.right || 20}mm;
              height: 15mm;
              display: flex;
              justify-content: space-between;
              align-items: center;
              font-family: 'Poppins', sans-serif;
              font-size: ${headerFooter.header?.fontSize || '10pt'};
              color: #0F3A61;
              border-bottom: 1px solid #f2c526;
              padding-bottom: 5px;
            }
            
            .page-footer {
              position: fixed;
              bottom: 0;
              left: ${margins.left || 20}mm;
              right: ${margins.right || 20}mm;
              height: 15mm;
              display: flex;
              justify-content: space-between;
              align-items: center;
              font-family: 'Poppins', sans-serif;
              font-size: ${headerFooter.footer?.fontSize || '10pt'};
              color: #4A6785;
              border-top: 1px solid #f2c526;
              padding-top: 5px;
            }
            
            .header-left, .footer-left {
              text-align: left;
              flex: 1;
            }
            
            .header-center, .footer-center {
              text-align: center;
              flex: 1;
            }
            
            .header-right, .footer-right {
              text-align: right;
              flex: 1;
            }
            
            .content {
              padding-top: ${headerFooter.enabled ? '20mm' : '0'};
              padding-bottom: ${headerFooter.enabled ? '20mm' : '0'};
            }
            ` : ''}
            
            /* Custom CSS */
            ${customCSS || ''}
            
            /* Extracted styles from content */
            ${extractedStyles || ''}
          </style>
        </head>
        <body>
          <div class="page-container">
            ${contentPages.map((pageContent, index) => `
              <div class="page" data-page="${index + 1}">
                ${headerFooter?.enabled ? `
                <div class="page-header">
                  <div class="header-left">${formatVariables(headerFooter.header?.left || '')}</div>
                  <div class="header-center">${formatVariables(headerFooter.header?.center || '')}</div>
                  <div class="header-right">${formatVariables(headerFooter.header?.right || '')}</div>
                </div>
                ` : ''}
                ${watermark.enabled ? `
                  <div class="watermark-container">
                    ${watermark.pattern?.enabled ? `
                      <div class="watermark-pattern">
                        ${(() => {
                          const density = watermark.pattern?.density || 'medium';
                          const densityMap = { low: 25, medium: 50, high: 75, maximum: 100 };
                          const count = densityMap[density];
                          return Array.from({ length: count }).map(() => 
                            watermark.image ? 
                              `<img src="${watermark.image}" class="watermark-image" alt="Watermark" />` :
                              `<div class="watermark">${watermark.text || 'CONFIDENTIAL'}</div>`
                          ).join('');
                        })()}
                      </div>
                    ` : (
                      watermark.image ? 
                        `<img src="${watermark.image}" class="watermark-image-single" alt="Watermark" />` :
                        `<div class="watermark-single">${watermark.text || 'CONFIDENTIAL'}</div>`
                    )}
                  </div>
                ` : ''}
                <div class="content page-content" data-page-content="${index + 1}">
                  ${pageContent}
                </div>
                ${config.pageNumbers !== false ? `
                <div class="page-number">Page <span class="current-page">${index + 1}</span> of <span class="total-pages">${contentPages.length}</span></div>
                ` : ''}
                ${headerFooter?.enabled ? `
                <div class="page-footer">
                  <div class="footer-left">${formatVariables(headerFooter.footer?.left || '')}</div>
                  <div class="footer-center">${formatVariables(headerFooter.footer?.center || '')}</div>
                  <div class="footer-right">${formatVariables(headerFooter.footer?.right || '')}</div>
                </div>
                ` : ''}
              </div>
            `).join('')}
          </div>
          <script>
            // Enhanced page handling and automatic content splitting
            (function() {
              const pages = document.querySelectorAll('.page');
              let totalPages = pages.length;
              
              // Enhanced automatic page splitting
              function handleAutomaticPageSplitting() {
                const pageHeightMm = ${pageHeight};
                const marginTopMm = ${margins.top || 20};
                const marginBottomMm = ${margins.bottom || 20};
                const contentHeightMm = pageHeightMm - marginTopMm - marginBottomMm;
                const contentHeightPx = contentHeightMm * 3.78; // 1mm ≈ 3.78px at 96dpi
                
                let hasOverflow = false;
                let additionalPages = 0;
                
                pages.forEach((page, index) => {
                  const pageContent = page.querySelector('.page-content');
                  if (!pageContent) return;
                  
                  // Temporarily remove height constraints to measure real content height
                  const originalStyles = {
                    height: page.style.height,
                    overflow: page.style.overflow,
                    minHeight: page.style.minHeight
                  };
                  
                  page.style.height = 'auto';
                  page.style.overflow = 'visible';
                  page.style.minHeight = 'auto';
                  
                  const realContentHeight = pageContent.scrollHeight;
                  
                  // Restore original styles
                  page.style.height = originalStyles.height;
                  page.style.overflow = originalStyles.overflow;
                  page.style.minHeight = originalStyles.minHeight;
                  
                  // DISABLED - Overflow detection not needed with automatic pagination
                  // Check if content overflows the page
                  /*
                  if (realContentHeight > contentHeightPx) {
                    hasOverflow = true;
                    
                    // Mark overflow pages with visual indicator
                    page.classList.add('overflow-page');
                    page.style.borderLeft = '4px solid #ff9800';
                    page.style.position = 'relative';
                    
                    // Add overflow warning
                    const warningEl = document.createElement('div');
                    warningEl.style.cssText = \`
                      position: absolute;
                      top: 5px;
                      left: 10px;
                      background: rgba(255, 152, 0, 0.9);
                      color: white;
                      padding: 4px 8px;
                      font-size: 11px;
                      border-radius: 3px;
                      z-index: 1000;
                      pointer-events: none;
                    \`;
                    warningEl.textContent = 'Content overflow - consider adding page breaks';
                    page.appendChild(warningEl);
                    
                    // Calculate how many additional pages we might need
                    const pagesNeeded = Math.ceil(realContentHeight / contentHeightPx) - 1;
                    additionalPages += pagesNeeded;
                  }
                  */
                });
                
                // Update total pages count
                totalPages += additionalPages;
                
                return hasOverflow;
              }
              
              // Run automatic page splitting
              const hasOverflow = handleAutomaticPageSplitting();
              
              // Send initial page info to parent
              if (window.parent && window.parent !== window) {
                window.parent.postMessage({
                  type: 'pageInfo',
                  currentPage: 1,
                  totalPages: totalPages,
                  hasOverflow: hasOverflow
                }, '*');
              }
              
              // Handle page visibility and navigation
              function updatePageVisibility(targetPage = 1) {
                pages.forEach((page, index) => {
                  const pageNumber = index + 1;
                  if (pageNumber === targetPage) {
                    page.style.display = 'flex';
                    page.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                });
                
                // Update page numbers if they exist
                document.querySelectorAll('.current-page').forEach(el => {
                  el.textContent = targetPage;
                });
                document.querySelectorAll('.total-pages').forEach(el => {
                  el.textContent = totalPages;
                });
              }
              
              // Listen for page navigation messages
              window.addEventListener('message', function(event) {
                if (event.data && event.data.type === 'navigateToPage') {
                  updatePageVisibility(event.data.page);
                }
              });
              
              // Initialize page visibility
              updatePageVisibility(1);
            })();
            
            // Original image processing script continues below

            // Apply all image attributes as inline styles for proper rendering
            document.querySelectorAll('img').forEach(img => {
              const width = img.getAttribute('width');
              const height = img.getAttribute('height');
              const align = img.getAttribute('data-align');
              const float = img.getAttribute('data-float');
              const marginTop = img.getAttribute('data-margin-top');
              const marginRight = img.getAttribute('data-margin-right');
              const marginBottom = img.getAttribute('data-margin-bottom');
              const marginLeft = img.getAttribute('data-margin-left');
              const borderWidth = img.getAttribute('data-border-width');
              const borderColor = img.getAttribute('data-border-color');
              const borderRadius = img.getAttribute('data-border-radius');
              const shadow = img.getAttribute('data-shadow');
              
              // Apply dimensions
              if (width) {
                img.style.width = width + 'px';
                img.style.maxWidth = 'none';
              }
              if (height) {
                img.style.height = height + 'px';
              }
              
              // Apply float or alignment
              if (float === 'left') {
                img.style.float = 'left';
                img.style.clear = 'left';
                img.style.display = 'inline';
              } else if (float === 'right') {
                img.style.float = 'right';
                img.style.clear = 'right';
                img.style.display = 'inline';
              } else {
                // Apply alignment only when not floating
                img.style.display = 'block';
                if (align === 'center') {
                  img.style.marginLeft = 'auto';
                  img.style.marginRight = 'auto';
                } else if (align === 'right') {
                  img.style.marginLeft = 'auto';
                  img.style.marginRight = '0';
                } else {
                  img.style.marginLeft = '0';
                  img.style.marginRight = 'auto';
                }
              }
              
              // Apply margins (after alignment to override if needed)
              if (marginTop || marginRight || marginBottom || marginLeft) {
                const mt = marginTop || '0';
                const mr = marginRight || '0';
                const mb = marginBottom || '0';
                const ml = marginLeft || '0';
                img.style.margin = mt + 'px ' + mr + 'px ' + mb + 'px ' + ml + 'px';
              }
              
              // Apply border
              if (borderWidth && borderWidth !== '0') {
                img.style.border = borderWidth + 'px solid ' + (borderColor || '#ccc');
                if (borderRadius) img.style.borderRadius = borderRadius + 'px';
              }
              
              // Apply shadow
              if (shadow && shadow !== 'none') {
                const shadowStyles = {
                  'small': '0 1px 3px rgba(0,0,0,0.12)',
                  'medium': '0 4px 6px rgba(0,0,0,0.15)',
                  'large': '0 10px 20px rgba(0,0,0,0.19)'
                };
                img.style.boxShadow = shadowStyles[shadow] || shadow;
              }
              
              // Ensure float images have proper default margins if not set
              if (float === 'left' && !marginRight) {
                img.style.marginRight = '15px';
                if (!marginBottom) img.style.marginBottom = '15px';
              } else if (float === 'right' && !marginLeft) {
                img.style.marginLeft = '15px';
                if (!marginBottom) img.style.marginBottom = '15px';
              }
            });
          </script>
          ${customJS && config.jsEnabled ? `<script>${customJS}</script>` : ''}
        </body>
      </html>
    `;
    
    return html;
  };
  
  // Handle message from iframe
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'pageInfo') {
        setCurrentPage(event.data.currentPage);
        setTotalPages(event.data.totalPages);
        
        // Handle overflow warning if present
        if (event.data.hasOverflow) {
          console.warn('Content overflow detected on some pages. Consider adding manual page breaks or adjusting content.');
        }
      }
    };
    
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);
  
  // Update preview - never show loading after initial
  const updatePreview = () => {
    if (!iframeRef.current) return;
    
    // NEVER show loading for updates, only for the very first load
    if (isInitialLoad && !iframeRef.current.srcdoc) {
      setIsLoading(true);
    }
    
    try {
      const html = generatePreviewHTML();
      const iframe = iframeRef.current;
      
      // Use srcdoc for better security and compatibility
      iframe.srcdoc = html;
      
      setError(null);
    } catch (err) {
      console.error('Preview error:', err);
      setError('Failed to generate preview');
      setIsLoading(false);
    }
  };
  
  // Initial load
  useEffect(() => {
    updatePreview();
  }, []);
  
  // Update total pages when content pages change
  useEffect(() => {
    const pages = splitPDFContent(content);
    setTotalPages(pages.length);
    console.log('Total pages updated:', pages.length);
  }, [content]);
  
  // Sync scroll to cursor position for exact tracking
  useEffect(() => {
    if (!iframeRef.current?.contentWindow || !cursorPosition) return;
    
    requestAnimationFrame(() => {
      try {
        const doc = iframeRef.current.contentDocument;
        if (!doc) return;
        
        // Use the page that the cursor is actually on (from improved tracking)
        const cursorPageNum = cursorPosition.page || currentEditorPage || 1;
        const pageElements = doc.querySelectorAll('.page');
        const targetPage = pageElements[cursorPageNum - 1];
        
        // Update the current page display to match cursor position
        if (cursorPageNum !== currentPage) {
          setCurrentPage(cursorPageNum);
        }
        
        if (targetPage) {
          // Get page content element for accurate positioning
          const pageContent = targetPage.querySelector('.page-content');
          if (!pageContent) return;
          
          // Get the absolute position of the page in the document
          const pageTop = targetPage.offsetTop;
          
          // For manual page breaks, we need to handle positioning differently
          // The position within page is already calculated accounting for breaks
          const cursorPositionInPage = Math.max(0, cursorPosition.positionInPage || 0);
          
          // Get the actual content height in the preview page
          const pageContentHeight = pageContent.scrollHeight || pageContent.offsetHeight;
          
          // For pages with manual breaks, use actual content proportions
          // Don't use fixed page height as content may be shorter
          let targetPositionInPreview;
          
          if (cursorPositionInPage < pageContentHeight) {
            // Direct mapping when position is within actual content
            targetPositionInPreview = pageTop + cursorPositionInPage;
          } else {
            // If position exceeds content (shouldn't happen), place at end
            targetPositionInPreview = pageTop + pageContentHeight - 100;
          }
          
          // Get current viewport state
          const viewportHeight = iframeRef.current.contentWindow.innerHeight;
          const currentScroll = iframeRef.current.contentWindow.scrollY;
          
          // Calculate where the cursor appears in the viewport
          const cursorViewportPosition = targetPositionInPreview - currentScroll;
          
          // Define a comfort zone (middle 60% of viewport)
          const upperBound = viewportHeight * 0.2;
          const lowerBound = viewportHeight * 0.8;
          
          // Only scroll if cursor is outside the comfort zone
          if (cursorViewportPosition < upperBound || cursorViewportPosition > lowerBound) {
            // Center the cursor in the viewport
            const idealScrollTop = targetPositionInPreview - (viewportHeight / 2);
            
            iframeRef.current.contentWindow.scrollTo({
              top: Math.max(0, idealScrollTop),
              left: 0,
              behavior: 'instant'
            });
          }
        }
      } catch (e) {
        console.error('Cursor sync error:', e);
      }
    });
  }, [currentEditorPage, cursorPosition]);
  
  // Auto refresh effect - update content without replacing entire iframe
  useEffect(() => {
    if (!autoRefresh) return;
    if (!iframeRef.current) return;
    
    const doc = iframeRef.current.contentDocument;
    if (!doc) {
      // If no document exists yet, do initial load
      const html = generatePreviewHTML();
      iframeRef.current.srcdoc = html;
      return;
    }
    
    // Check if we have the page container
    const pagesContainer = doc.querySelector('body');
    if (!pagesContainer) {
      // No body, do full reload
      const html = generatePreviewHTML();
      iframeRef.current.srcdoc = html;
      return;
    }
    
    // Split content into pages
    const contentPages = splitPDFContent(content);
    const existingPages = doc.querySelectorAll('.page');
    
    // If page count changed significantly, do full reload
    if (Math.abs(existingPages.length - contentPages.length) > 1) {
      const html = generatePreviewHTML();
      iframeRef.current.srcdoc = html;
      return;
    }
    
    // Update existing pages content only
    existingPages.forEach((page, index) => {
      const pageContent = page.querySelector('.page-content');
      if (pageContent && contentPages[index]) {
        // Only update if content actually changed
        if (pageContent.innerHTML !== contentPages[index]) {
          pageContent.innerHTML = contentPages[index];
        }
      }
    });
    
    // Add new page if needed
    if (contentPages.length > existingPages.length) {
      const lastPage = existingPages[existingPages.length - 1];
      if (lastPage) {
        const newPage = lastPage.cloneNode(true);
        const pageContent = newPage.querySelector('.page-content');
        if (pageContent) {
          pageContent.innerHTML = contentPages[contentPages.length - 1];
        }
        lastPage.parentNode.insertBefore(newPage, lastPage.nextSibling);
      }
    }
    
    // Remove extra page if needed
    if (contentPages.length < existingPages.length && existingPages.length > 1) {
      existingPages[existingPages.length - 1].remove();
    }
    
  }, [content, autoRefresh]);
  
  // Handle CSS/JS/Config changes separately - these require full reload
  useEffect(() => {
    if (!iframeRef.current) return;
    if (!iframeRef.current.contentDocument) return; // Skip on initial load
    
    // For CSS, JS, or config changes, we need to reload the entire preview
    const html = generatePreviewHTML();
    iframeRef.current.srcdoc = html;
    
  }, [customCSS, customJS, config]);
  
  // Handle zoom
  const handleZoom = (newZoom) => {
    setZoom(newZoom);
    // Zoom is now handled in the iframe style prop directly
  };
  
  // Handle page navigation
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      // Send navigation message to iframe
      if (iframeRef.current && iframeRef.current.contentWindow) {
        try {
          iframeRef.current.contentWindow.postMessage({
            type: 'navigateToPage',
            page: page
          }, '*');
        } catch (e) {
          // Fallback: try to scroll directly
          console.log('Could not send message, trying direct scroll:', e);
          try {
            const pageElement = iframeRef.current.contentWindow.document.querySelector(`[data-page="${page}"]`);
            if (pageElement) {
              pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          } catch (scrollError) {
            console.log('Could not scroll to page:', scrollError);
          }
        }
      }
    }
  };
  
  // Manual refresh
  const handleRefresh = () => {
    // Don't show loading indicator for manual refresh to avoid flicker
    updatePreview();
  };
  
  // Fit to screen
  const handleFitScreen = () => {
    if (iframeRef.current && iframeRef.current.parentElement) {
      const container = iframeRef.current.parentElement;
      const containerWidth = container.clientWidth - 40; // Account for padding
      
      // Get the page dimensions from config
      const { pageSize = 'A4', orientation = 'portrait' } = config;
      const pageSizes = {
        A4: { width: 210, height: 297 },
        Letter: { width: 216, height: 279 },
        Legal: { width: 216, height: 356 }
      };
      
      const dimensions = pageSizes[pageSize] || pageSizes.A4;
      const pageWidthMm = orientation === 'portrait' ? dimensions.width : dimensions.height;
      const pageWidthPx = pageWidthMm * 3.78; // Convert mm to px
      
      // Calculate scale to fit width
      const scale = Math.floor((containerWidth / pageWidthPx) * 100);
      
      // Set zoom to fit, but don't exceed 100%
      handleZoom(Math.min(scale, 100));
    }
  };
  
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <Paper sx={{ borderRadius: 0 }} elevation={1}>
        <Toolbar variant="dense">
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            Preview
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {/* Page Navigation */}
            <ToolbarGroup>
              <ToolbarButton
                icon={<FirstPageIcon />}
                tooltip="First page"
                onClick={() => handlePageChange(1)}
                disabled={currentPage <= 1}
                size="small"
              />
              
              <ToolbarButton
                icon={<PrevIcon />}
                tooltip="Previous page"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                size="small"
              />
              
              <Typography variant="caption" sx={{ minWidth: 80, textAlign: 'center', px: 1 }}>
                Page {currentPage} / {totalPages}
              </Typography>
              
              <ToolbarButton
                icon={<NextIcon />}
                tooltip="Next page"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                size="small"
              />
              
              <ToolbarButton
                icon={<LastPageIcon />}
                tooltip="Last page"
                onClick={() => handlePageChange(totalPages)}
                disabled={currentPage >= totalPages}
                size="small"
              />
            </ToolbarGroup>
            
            <ToolbarDivider />
            
            <ToolbarGroup>
              <ToolbarButton
                icon={<ZoomOutIcon />}
                tooltip="Zoom out"
                onClick={() => handleZoom(Math.max(25, zoom - 10))}
                disabled={zoom <= 25}
                size="small"
              />
              
              <Typography variant="caption" sx={{ minWidth: 50, textAlign: 'center', px: 1 }}>
                {zoom}%
              </Typography>
              
              <ToolbarButton
                icon={<ZoomInIcon />}
                tooltip="Zoom in"
                onClick={() => handleZoom(Math.min(200, zoom + 10))}
                disabled={zoom >= 200}
                size="small"
              />
            </ToolbarGroup>
            
            <ToolbarDivider />
            
            <ToolbarGroup>
              <ToolbarButton
                icon={<FitScreenIcon />}
                tooltip="Fit to screen"
                onClick={handleFitScreen}
                size="small"
              />
              
              <ToolbarButton
                icon={<RefreshIcon />}
                tooltip="Refresh preview"
                onClick={handleRefresh}
                size="small"
              />
            </ToolbarGroup>
          </Box>
        </Toolbar>
        
        {isLoading && <LinearProgress />}
      </Paper>
      
      {/* Preview content */}
      <Box sx={{ 
        flex: 1, 
        overflow: 'hidden', // Changed from 'auto' to 'hidden' to prevent double scrollbars
        bgcolor: 'grey.100',
        position: 'relative'
      }}>
        {error ? (
          <Alert severity="error" icon={<ErrorIcon />} sx={{ m: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box sx={{ 
            width: '100%',
            height: '100%',
            overflow: 'auto', // Scroll only on this container
            display: 'flex',
            justifyContent: 'center',
            p: 2
          }}>
            <iframe
              ref={iframeRef}
              title="PDF Preview"
              onLoad={() => {
                setIsLoading(false);
                setIsInitialLoad(false);
              }}
              style={{
                border: 'none',
                width: '100%',
                height: '100%',
                background: 'white',
                transform: `scale(${zoom / 100})`,
                transformOrigin: 'top center',
                transition: 'transform 0.2s'
              }}
              sandbox="allow-scripts allow-same-origin"
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default PDFPreview;